import React, { useState, useEffect } from 'react';
import {
  Card,
  ListGroup,
  Row,
  Col,
  Spinner,
  Alert,
  Tab,
  Tabs,
  Button,
  Form
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import ChangePasswordCard from './ChangePasswordCard';
import './Profile.css';

const Profile = ({userId}) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSecurityTab, setShowSecurityTab] = useState(false); // New state
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    contact: '',
    zipcode: '',
    dob: '',
    gender: '',
    enrollment_for: '',
  });
  const { bearerAuth } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get("tab");
  const showSecurity = queryParams.get("security") === "true"; // Check security param

  const [activeTab, setActiveTab] = useState(tabFromUrl || "personal");
  
  // Set showSecurityTab based on URL parameter
  useEffect(() => {
    if (showSecurity) {
      setShowSecurityTab(true);
      // Agar security parameter true hai to active tab bhi security set karo
      if (tabFromUrl === "security") {
        setActiveTab("security");
      }
    } else {
      setShowSecurityTab(false);
      // Agar security parameter nahi hai aur active tab security hai to personal par set karo
      if (activeTab === "security") {
        setActiveTab("personal");
      }
    }
  }, [showSecurity, tabFromUrl]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const { updateUser, user, viewMode } = useAuth();
  
  const fetchUserData = async () => {
    try {
      const response = await bearerAuth(viewMode==='user'?`myinfo`:`driving-school/users/${userId}`);
      if (response.data && response.data.data) {
        updateUser(response.data.data);
        setUserData(response.data.data);
        setEditForm({
          full_name: response.data.data.full_name || '',
          email: response.data.data.email || '',
          contact: response.data.data.contact || '',
          zipcode: response.data.data.zipcode != null ? String(response.data.data.zipcode) : '',
          dob: response.data.data.dob || '',
          gender: response.data.data.gender || '',
          enrollment_for: response.data.data.enrollment_for || '',
        });
      } else {
        throw new Error('No user data received from server');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [user?.id]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStartEdit = () => {
    setSuccess('');
    setError(null);
    setEditForm({
      full_name: userData?.full_name || '',
      email: userData?.email || '',
      contact: userData?.contact || '',
      zipcode: userData?.zipcode != null ? String(userData.zipcode) : '',
      dob: userData?.dob || '',
      gender: userData?.gender || '',
      enrollment_for: userData?.enrollment_for || '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSuccess('');
    setError(null);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess('');
    setIsSaving(true);
    try {
      const payload = {
        user_id: userData?.id,
        enrollment_id: userData?.enrollment_id,
        full_name: editForm.full_name,
        email: editForm.email,
        contact: editForm.contact,
        zipcode: editForm.zipcode != null ? String(editForm.zipcode) : '',
        dob: editForm.dob || null,
        gender: editForm.gender || null,
        enrollment_for: editForm.enrollment_for || null,
      };
      const response = await bearerAuth('edit-user', 'POST', payload);
      const updated = response?.data?.data || {};
      setUserData((prev) => ({
        ...prev,
        ...updated,
        dob: updated.dob ?? editForm.dob ?? prev?.dob,
        gender: updated.gender ?? editForm.gender ?? prev?.gender,
        enrollment_for: updated.enrollment_for ?? editForm.enrollment_for ?? prev?.enrollment_for,
      }));
      updateUser(updated);
      setIsEditing(false);
      setSuccess(response?.data?.message || 'Profile updated successfully');
    } catch (err) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        Error loading profile: {error}
      </Alert>
    );
  }

  if (!userData) {
    return (
      <Alert variant="warning" className="mt-3">
        No profile data available
      </Alert>
    );
  }

  const enrollStatus = String(userData.enroll_status || "").toLowerCase();
  const isEnrollmentAccepted = enrollStatus === "2" || enrollStatus === "accepted";
  const displayStartDate = isEnrollmentAccepted
    ? (userData.admin_exact_start_at || userData.expected_start_date)
    : userData.expected_start_date;
  const displayTimeSlot = isEnrollmentAccepted
    ? (userData.admin_time_slot || userData.time_slot)
    : userData.time_slot;

  return (
    <div className="profile-container profile-page-modern">
      {/* Header and Course Button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="profile-title">MY PROFILE</h2>

        {/* Show button only if course not already selected and in user mode (not school view) */}
        {!userData.CourseName && viewMode === 'user' && (
          <Button
            variant="primary"
            onClick={() =>
              navigate('/enroll', {
                state: {
                  closeTo: '/dashboard/profile',
                  successTo: '/dashboard/profile',
                  origin: 'profile',
                },
              })
            }
          >
            Select Course
          </Button>
        )}
      </div>

      {/* Tabs Section - Conditional rendering based on showSecurityTab */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        id="profile-tabs"
        className="mb-4 profile-tabs-modern"
      >
        {/* Always show these three tabs */}
        <Tab eventKey="personal" title="Personal Info">
          {/* Personal Info content - same as before */}
          <Card className="mt-3 profile-card-modern">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Personal Information</h5>
              {viewMode === 'user' && !isEditing && (
                <Button variant="outline-primary" size="sm" className="profile-edit-btn" onClick={handleStartEdit}>
                  Edit Information
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {success && (
                <Alert variant="success" className="mb-3">
                  {success}
                </Alert>
              )}
              {!isEditing ? (
                <ListGroup variant="flush">
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label"> Student Id:</Col><Col md={9} className="profile-info-value">{userData.username}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Full Name:</Col><Col md={9} className="profile-info-value">{userData.full_name}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Email:</Col><Col md={9} className="profile-info-value">{userData.email}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Contact Number:</Col><Col md={9} className="profile-info-value">{userData.contact}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Zip Code:</Col><Col md={9} className="profile-info-value">{userData.zipcode}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Date of Birth:</Col><Col md={9} className="profile-info-value">{userData.dob || 'Not provided'}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Gender:</Col><Col md={9} className="profile-info-value">{userData.gender || 'Not provided'}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item className="profile-info-row">
                    <Row><Col md={3} className="profile-info-label">Enrollment For:</Col><Col md={9} className="profile-info-value">{userData.enrollment_for || 'Not provided'}</Col></Row>
                  </ListGroup.Item>
                </ListGroup>
              ) : (
                <Form onSubmit={handleSaveProfile}>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Student Id:</Col>
                    <Col md={9}>
                      <Form.Control value={userData.username || ''} disabled />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Full Name:</Col>
                    <Col md={9}>
                      <Form.Control
                        name="full_name"
                        value={editForm.full_name}
                        onChange={handleEditChange}
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Email:</Col>
                    <Col md={9}>
                      <Form.Control
                        type="email"
                        name="email"
                        value={editForm.email}
                        disabled
                        readOnly
                      />
                      <Form.Text className="text-muted">Email is locked and can be changed only via verification flow.</Form.Text>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Contact Number:</Col>
                    <Col md={9}>
                      <Form.Control
                        name="contact"
                        value={editForm.contact}
                        onChange={handleEditChange}
                        placeholder="10 digit mobile number"
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Zip Code:</Col>
                    <Col md={9}>
                      <Form.Control
                        name="zipcode"
                        value={editForm.zipcode}
                        onChange={handleEditChange}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Date of Birth:</Col>
                    <Col md={9}>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={editForm.dob || ''}
                        onChange={handleEditChange}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Gender:</Col>
                    <Col md={9}>
                      <Form.Select
                        name="gender"
                        value={editForm.gender || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold d-flex align-items-center">Enrollment For:</Col>
                    <Col md={9}>
                      <Form.Select
                        name="enrollment_for"
                        value={editForm.enrollment_for || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select type</option>
                        <option value="self">Self</option>
                        <option value="parent">Parent</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Col>
                  </Row>
                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="course" title="Course Details">
          <Card className="mt-3 profile-card-modern">
            <Card.Header><h5>Course Information</h5></Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Row><Col md={3} className="fw-bold">Course Name:</Col><Col md={9}>{userData.CourseName || 'Not enrolled'}</Col></Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row><Col md={3} className="fw-bold">Description:</Col><Col md={9}>{userData.CourseDescription || 'Not available'}</Col></Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row><Col md={3} className="fw-bold">Duration:</Col><Col md={9}>{userData.duration ? `${userData.duration} day` : 'Not specified'}</Col></Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row><Col md={3} className="fw-bold">Price:</Col><Col md={9}>{userData.CoursePrice ? `₹${userData.CoursePrice}` : 'Not specified'}</Col></Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row><Col md={3} className="fw-bold">{isEnrollmentAccepted ? 'Exact Start Date:' : 'Expected Start Date:'}</Col><Col md={9}>{displayStartDate || 'Not scheduled'}</Col></Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row><Col md={3} className="fw-bold">{isEnrollmentAccepted ? 'Admin Time Slot:' : 'Time Slot:'}</Col><Col md={9}>{displayTimeSlot || 'Not scheduled'}</Col></Row>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="school" title="Driving School">
          <Card className="mt-3 profile-card-modern">
            <Card.Header><h5>Driving School Information</h5></Card.Header>
            <Card.Body>
              {isEnrollmentAccepted ? (
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <Row><Col md={3} className="fw-bold">School Name:</Col><Col md={9}>{userData.driving_school_name || 'Not assigned'}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row><Col md={3} className="fw-bold">Address:</Col><Col md={9}>{userData.driving_school_address || 'Not available'}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row><Col md={3} className="fw-bold">Contact Email:</Col><Col md={9}>{userData.driving_school_email || 'Not available'}</Col></Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row><Col md={3} className="fw-bold">Contact Number:</Col><Col md={9}>{userData.driving_school_number || 'Not available'}</Col></Row>
                  </ListGroup.Item>
                </ListGroup>
              ) : (
                <Alert variant="info" className="mb-0">
                  Your enrollment is pending approval from the driving school. School details will be visible after acceptance.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Security Tab - Only show when showSecurityTab is true */}
        {showSecurityTab && viewMode === 'user' && (
          <Tab eventKey="security" title="Security">
            <div className="mt-3">
              <ChangePasswordCard endpoint="change-password" />
            </div>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default Profile;
